import * as Sentry from '@sentry/vue'
import { App } from 'vue'
import { Router } from 'vue-router'

export function setupSentry(app: App, router: Router): void {
  const dsn = import.meta.env['VITE_SENTRY_DSN']

  if (!dsn) {
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.')
    return
  }

  Sentry.init({
    app,
    dsn,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
    ],

    // Environment and release
    environment: import.meta.env['VITE_APP_ENV'] || 'development',
    release: import.meta.env['VITE_SENTRY_RELEASE'] || '1.0.0',

    // Sampling
    tracesSampleRate: parseFloat(import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'] || '0.1'),
    profilesSampleRate: parseFloat(import.meta.env['VITE_SENTRY_PROFILES_SAMPLE_RATE'] || '0.1'),

    // Debug mode
    debug: import.meta.env['VITE_SENTRY_DEBUG'] === 'true',

    // Before send callback
    beforeSend(event, hint) {
      // Ignore specific errors
      if (hint.originalException instanceof Error) {
        const error = hint.originalException

        // Ignore network errors
        const errorCode = (error as { code?: string }).code
        if (
          errorCode === 'ERR_NETWORK' ||
          errorCode === 'ECONNABORTED' ||
          error.message.includes('Network Error') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('timeout')
        ) {
          return null
        }

        // Ignore specific error patterns
        const ignoredPatterns = [
          'ResizeObserver loop limit exceeded',
          'Script error',
          'Script error.',
          'Uncaught ReferenceError:',
        ]

        for (const pattern of ignoredPatterns) {
          if (error.message.includes(pattern)) {
            return null
          }
        }
      }

      // Add custom context
      event.contexts = {
        ...event.contexts,
        app: {
          version: import.meta.env['VITE_SENTRY_RELEASE'] || 'unknown',
          environment: import.meta.env['VITE_APP_ENV'] || 'unknown',
        },
      }

      return event
    },

    // Ignore specific URLs
    ignoreTransactions: ['/health', '/ping', '/favicon.ico', '/robots.txt', '/sitemap.xml'],

    // Max breadcrumbs
    maxBreadcrumbs: 100,

    // Performance
    sendDefaultPii: false,
    attachStacktrace: true,

    // Sampling
    sampleRate: parseFloat(import.meta.env['VITE_SENTRY_SAMPLE_RATE'] || '1.0'),
  })
}

// Sentry service for manual error reporting
export class SentryService {
  /**
   * Capture exception
   */
  static captureException(error: Error, context?: Record<string, unknown>): void {
    Sentry.captureException(error, context)
  }

  /**
   * Capture message
   */
  static captureMessage(
    message: string,
    level: Sentry.SeverityLevel = 'info',
    context?: Record<string, unknown>,
  ): void {
    if (context) {
      Sentry.withScope((scope) => {
        scope.setContext('additional', context)
        Sentry.captureMessage(message, level)
      })
    } else {
      Sentry.captureMessage(message, level)
    }
  }

  /**
   * Set user context
   */
  static setUser(user: { id: string; email?: string; username?: string } | null): void {
    Sentry.setUser(user)
  }

  /**
   * Set tags
   */
  static setTags(tags: Record<string, string>): void {
    Sentry.setTags(tags)
  }

  /**
   * Set context
   */
  static setContext(name: string, data: Record<string, unknown>): void {
    Sentry.setContext(name, data)
  }

  /**
   * Start span
   */
  static startSpan(name: string, operation: string = 'custom'): unknown {
    try {
      // Use the modern Sentry.startSpan API with callback
      return Sentry.startSpan({ name, op: operation }, (span) => {
        return span
      })
    } catch (error) {
      console.warn('Failed to start Sentry span:', error)
      return null
    }
  }

  /**
   * Add breadcrumb
   */
  static addBreadcrumb(
    message: string,
    category: string = 'default',
    data?: Record<string, unknown>,
  ): void {
    Sentry.addBreadcrumb({
      message,
      category,
      data: data ?? {},
    })
  }

  /**
   * Set release
   */
  static setRelease(release: string): void {
    Sentry.setTag('release', release)
  }

  /**
   * Set environment
   */
  static setEnvironment(environment: string): void {
    Sentry.setTag('environment', environment)
  }

  /**
   * Flush events
   */
  static async flush(timeout: number = 2000): Promise<boolean> {
    return Sentry.flush(timeout)
  }

  // Helpers for current scope/span можно добавить при необходимости, избегая прямых вызовов недоступных API
}

function isNoisyClientError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }

  const errorMessage = error.message || error.toString()
  const errorStack = error.stack || ''

  return (
    errorMessage.includes('contentscript.js') ||
    errorStack.includes('contentscript.js') ||
    errorMessage.includes('Cannot read properties of null') ||
    errorMessage.includes('querySelector')
  )
}

// Vue error handler - must not be overwritten after setup (see bootstrapApp).
export function setupVueErrorHandler(app: App): void {
  app.config.errorHandler = (error, instance, info) => {
    if (isNoisyClientError(error)) {
      return
    }

    console.error('Vue Error:', error, info)

    SentryService.captureException(error as Error, {
      vue: {
        component: instance?.$options?.name || 'Unknown',
        info,
      },
    })
  }
}

// Global error handler - registered once; filters extension noise before Sentry.
export function setupGlobalErrorHandler(): void {
  window.addEventListener('error', (event) => {
    if (isNoisyClientError(event.error)) {
      return
    }

    SentryService.captureException(event.error, {
      global: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    if (isNoisyClientError(reason)) {
      return
    }

    const error = reason instanceof Error ? reason : new Error(String(reason))
    SentryService.captureException(error, {
      global: {
        type: 'unhandledrejection',
        reason,
      },
    })
  })
}
